import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchBudgetMonth, saveBudgetMonth } from './repository'
import { setAssigned, moveMoney, fundAllUnderfunded, updateCategoryGoal, type GoalFields } from './calc'
import { starterCategoryGroups } from './starter-categories'
import { priorMonthKeys } from './quick-budget'
import { afterMonthMutation } from './rollover-hooks'
import type { BudgetMonth, CategoryGroup } from './types'

/**
 * TanStack Query hooks for the budget month.
 *
 * Query key shape: ['budget', year, month]. The query function never
 * throws on "doc not found" — it returns null so the empty-state UI
 * can render an "Add categories" CTA instead of an error.
 *
 * Mutations are optimistic: we patch the cached `BudgetMonth` before
 * the Firestore write completes. Inline-assign is the hot path and
 * waiting on a round-trip would feel laggy. If the server-side write
 * fails, the cache is rolled back via `onError`.
 */

function monthKey(year: number, month: number) {
  return ['budget', year, month] as const
}

export function useBudgetMonth(year: number, month: number) {
  return useQuery({
    queryKey: monthKey(year, month),
    queryFn: () => fetchBudgetMonth(year, month),
    staleTime: 30_000,
  })
}

/**
 * Fires `count` parallel queries for the months preceding (year, month).
 * Used by the Quick-Budget menu to compute Last Month / Average values
 * without piling on additional onSnapshot listeners. Cached per (y, m)
 * so toggling between months doesn't re-fetch the same prior windows.
 *
 * Stale time is longer than the active month — prior months rarely
 * change once a month is closed.
 */
export function usePriorBudgetMonths(year: number, month: number, count = 3) {
  const keys = priorMonthKeys(year, month, count)
  return useQueries({
    queries: keys.map((k) => ({
      queryKey: monthKey(k.year, k.month),
      queryFn: () => fetchBudgetMonth(k.year, k.month),
      staleTime: 5 * 60_000,
    })),
  })
}

interface AssignArgs {
  year: number
  month: number
  categoryId: string
  newAssigned: number
}

export function useAssignMoney() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: AssignArgs) => {
      const current = qc.getQueryData<BudgetMonth | null>(monthKey(args.year, args.month))
      if (!current) throw new Error('No budget loaded')
      const result = setAssigned(current, args.categoryId, args.newAssigned)
      if (!result.ok) throw new Error(result.error.kind)
      await saveBudgetMonth(result.value)
      return result.value
    },
    onMutate: async (args) => {
      await qc.cancelQueries({ queryKey: monthKey(args.year, args.month) })
      const previous = qc.getQueryData<BudgetMonth | null>(monthKey(args.year, args.month))
      if (previous) {
        const result = setAssigned(previous, args.categoryId, args.newAssigned)
        if (result.ok) {
          qc.setQueryData(monthKey(args.year, args.month), result.value)
        }
      }
      return { previous }
    },
    onError: (_err, args, context) => {
      if (context?.previous) {
        qc.setQueryData(monthKey(args.year, args.month), context.previous)
      }
    },
    onSettled: (_data, _err, args) => afterMonthMutation(qc, args.year, args.month),
  })
}

interface MoveArgs {
  year: number
  month: number
  fromId: string | null
  toId: string | null
  amount: number
}

export function useMoveMoney() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: MoveArgs) => {
      const current = qc.getQueryData<BudgetMonth | null>(monthKey(args.year, args.month))
      if (!current) throw new Error('No budget loaded')
      const result = moveMoney(current, args.fromId, args.toId, args.amount)
      if (!result.ok) throw new Error(result.error.kind)
      await saveBudgetMonth(result.value)
      return result.value
    },
    onSettled: (_data, _err, args) => afterMonthMutation(qc, args.year, args.month),
  })
}

interface SeedArgs {
  year: number
  month: number
}

interface UpdateGoalArgs {
  year: number
  month: number
  categoryId: string
  fields: GoalFields
}

/** Update a single category's goal config and re-derive its
 *  targetUnderfunded. Optimistic; rolls back on error. */
export function useUpdateCategoryGoal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: UpdateGoalArgs) => {
      const current = qc.getQueryData<BudgetMonth | null>(monthKey(args.year, args.month))
      if (!current) throw new Error('No budget loaded')
      const result = updateCategoryGoal(current, args.categoryId, args.fields)
      if (!result.ok) throw new Error(result.error.kind)
      await saveBudgetMonth(result.value)
      return result.value
    },
    onMutate: async (args) => {
      await qc.cancelQueries({ queryKey: monthKey(args.year, args.month) })
      const previous = qc.getQueryData<BudgetMonth | null>(monthKey(args.year, args.month))
      if (previous) {
        const r = updateCategoryGoal(previous, args.categoryId, args.fields)
        if (r.ok) qc.setQueryData(monthKey(args.year, args.month), r.value)
      }
      return { previous }
    },
    onError: (_err, args, ctx) => {
      if (ctx?.previous) qc.setQueryData(monthKey(args.year, args.month), ctx.previous)
    },
    onSettled: (_data, _err, args) => {
      void qc.invalidateQueries({ queryKey: monthKey(args.year, args.month) })
    },
  })
}

interface FundAllArgs {
  year: number
  month: number
}

/** One-click "fund every underfunded-by-goal category to its target." */
export function useFundAllUnderfunded() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: FundAllArgs) => {
      const current = qc.getQueryData<BudgetMonth | null>(monthKey(args.year, args.month))
      if (!current) throw new Error('No budget loaded')
      const next = fundAllUnderfunded(current)
      await saveBudgetMonth(next)
      return next
    },
    onMutate: async (args) => {
      await qc.cancelQueries({ queryKey: monthKey(args.year, args.month) })
      const previous = qc.getQueryData<BudgetMonth | null>(monthKey(args.year, args.month))
      if (previous) {
        qc.setQueryData(monthKey(args.year, args.month), fundAllUnderfunded(previous))
      }
      return { previous }
    },
    onError: (_err, args, context) => {
      if (context?.previous) {
        qc.setQueryData(monthKey(args.year, args.month), context.previous)
      }
    },
    onSettled: (_data, _err, args) => afterMonthMutation(qc, args.year, args.month),
  })
}

export function useSeedStarterCategories() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: SeedArgs) => {
      const current = qc.getQueryData<BudgetMonth | null>(monthKey(args.year, args.month))
      const month: BudgetMonth = current ?? {
        id: crypto.randomUUID(),
        year: args.year,
        month: args.month,
        categoryGroups: [],
        income: 0,
        budgeted: 0,
        activity: 0,
        available: 0,
        carryOver: 0,
        ageOfMoney: null,
      }
      // Idempotent: don't double-seed if the month already has any
      // categories. Mirrors iOS BudgetManager.createStarterCategories.
      if (month.categoryGroups.length > 0) return month
      const seeded: BudgetMonth = { ...month, categoryGroups: starterCategoryGroups() }
      await saveBudgetMonth(seeded)
      return seeded
    },
    onSuccess: (seeded, args) => {
      qc.setQueryData(monthKey(args.year, args.month), seeded)
    },
  })
}

interface AddCategoryArgs {
  year: number
  month: number
  groupId: string
  name: string
}

export function useAddCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: AddCategoryArgs) => {
      const current = qc.getQueryData<BudgetMonth | null>(monthKey(args.year, args.month))
      if (!current) throw new Error('No budget loaded')
      const newCategory = {
        id: crypto.randomUUID(),
        name: args.name,
        groupId: args.groupId,
        sortOrder:
          (current.categoryGroups.find((g) => g.id === args.groupId)?.categories.length ?? 0) + 1,
        isHidden: false,
        assigned: 0,
        activity: 0,
        available: 0,
        carryOver: 0,
        goalId: null,
        note: null,
        isCreditCardPayment: false,
      }
      const updated: BudgetMonth = {
        ...current,
        categoryGroups: current.categoryGroups.map((g) =>
          g.id === args.groupId ? { ...g, categories: [...g.categories, newCategory] } : g,
        ),
      }
      await saveBudgetMonth(updated)
      return updated
    },
    onSuccess: (updated, args) => {
      qc.setQueryData(monthKey(args.year, args.month), updated)
    },
  })
}

interface AddGroupArgs {
  year: number
  month: number
  name: string
}

export function useAddGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: AddGroupArgs) => {
      const current = qc.getQueryData<BudgetMonth | null>(monthKey(args.year, args.month))
      if (!current) throw new Error('No budget loaded')
      const newGroup: CategoryGroup = {
        id: crypto.randomUUID(),
        name: args.name,
        sortOrder: current.categoryGroups.length,
        isHidden: false,
        categories: [],
      }
      const updated: BudgetMonth = {
        ...current,
        categoryGroups: [...current.categoryGroups, newGroup],
      }
      await saveBudgetMonth(updated)
      return updated
    },
    onSuccess: (updated, args) => {
      qc.setQueryData(monthKey(args.year, args.month), updated)
    },
  })
}
